// MeldUtils.js
import * as d3 from 'd3';

// Node size configuration
export const NodeConfig = {
    baseSize: 60,
    textHeight: 14,
    textWrapWidth: 100,
    padding: 15,
    cornerRadius: 10,
    lineHeight: 1.4,
    minCollidePad: 10,
    supportRadiusIncrease: 5,
    normalEvidenceFillColor: '#3399cc',
    fullyLinkedFillColor: '#4CAF50',
    getDisplayText: (node, languageContext) => {
        if (!languageContext) {
            return node.id;
        }
        if (languageContext.currentLanguage === 'en') {
            return node.id;
        }
        const translation = languageContext.getNodeTranslation?.(node);
        return translation?.title || node.id;
    },
    getDisplayDescription: (node, languageContext) => {
        if (!languageContext) return node.description;
        if (languageContext.currentLanguage === 'en') {
            return node.description;
        }
        const translation = languageContext.getNodeTranslation?.(node);
        return translation?.description || node.description;
    }
};

/**
 * Centralized link type definitions
 */
export const LinkTypes = {
    STRONGLY_SUPPORTS: {
        value: 2,
        label: 'strongly supports'
    },
    SUPPORTS: {
        value: 1,
        label: 'supports'
    },
    CONTRADICTS: {
        value: -2,
        label: 'contradicts'
    },
    NO_EFFECT_EXPLICIT: {
        value: -1,
        label: 'no effect'
    },
    NO_LINK: {
        value: 0,
        label: 'unlinked'
    }
};

/**
 * Helper functions for checking link states
 */
export const isExplicitNoEffect = (link) => link && link.value === LinkTypes.NO_EFFECT_EXPLICIT.value;
export const isUnlinked = (link) => !link || link.value === LinkTypes.NO_LINK.value;
export const isValidLink = (link) => link && link.value !== LinkTypes.NO_LINK.value;

/**
 * Maps link values to their corresponding labels
 */
export const getLinkStatusFromValue = (value) => {
    if (value === LinkTypes.NO_EFFECT_EXPLICIT.value) {
        return 'no effect';
    }
    const found = Object.values(LinkTypes).find(type => type.value === value);
    return found?.label || LinkTypes.NO_LINK.label;
};

export class TooltipManager {
    constructor(languageContext) {
        this.tooltip = d3.select("body").append("div")
            .attr("class", "tooltip")
            .style("opacity", 0)
            .style("position", "absolute")
            .style("background-color", "white")
            .style("border", "solid")
            .style("border-width", "1px")
            .style("border-radius", "5px")
            .style("padding", "10px")
            .style("pointer-events", "none");
        this.languageContext = languageContext;
    }

    show(event, node, nodes, links) {
        const displayContent = NodeConfig.getDisplayDescription(node, this.languageContext);
        const isFullyLinked = node.type === 'evidence' ?
            isEvidenceFullyLinked(node, nodes, links) : false;

        this.tooltip.transition()
            .duration(200)
            .style("opacity", .9);

        let content = displayContent;
        if (node.type === 'evidence' && isFullyLinked) {
            content += '<br><br><em>(Fully Linked)</em>';
        }

        this.tooltip.html(content)
            .style("left", (event.pageX + 50) + "px")
            .style("top", (event.pageY - 68) + "px");
    }

    hide() {
        this.tooltip.transition()
            .duration(200)
            .style("opacity", 0);
    }

    cleanup() {
        this.tooltip.remove();
    }

    setData(nodes, links) {
        this.allNodes = nodes;
        this.links = links;
    }
}

/**
 * Text wrapping utility for SVG text elements
 */
export const wrapText = (text, width = NodeConfig.textWrapWidth) => {
    text.each(function() {
        const text = d3.select(this);
        const words = text.text().split(/\s+/).reverse();
        const lineHeight = NodeConfig.lineHeight;
        const y = text.attr("y") || 0;
        const dy = parseFloat(text.attr("dy") || 0);
        let word;
        let line = [];
        let lineNumber = 0;

        let tspan = text.text(null)
            .append("tspan")
            .attr("x", 0)
            .attr("y", y)
            .attr("dy", dy + "em");

        while (word = words.pop()) {
            line.push(word);
            tspan.text(line.join(" "));

            if (tspan.node().getComputedTextLength() > width && line.length > 1) {
                line.pop();
                tspan.text(line.join(" "));
                line = [word];
                tspan = text.append("tspan")
                    .attr("x", 0)
                    .attr("y", y)
                    .attr("dy", ++lineNumber * lineHeight + dy + "em")
                    .text(word);
            }
        }
    });
};

/**
 * Determines if an evidence node has links to all models (including explicit no effect)
 */
export const isEvidenceFullyLinked = (evidenceNode, allNodes, links) => {
    if (!evidenceNode || !allNodes || !links || evidenceNode.type !== 'evidence') return false;

    // Count total models
    const modelNodes = allNodes.filter(node => node.type === 'model');
    if (modelNodes.length === 0) return false;

    // Check if we have valid links (including NO_EFFECT_EXPLICIT) to all models
    const linkedModels = new Set();

    links.forEach(link => {
        if (link.value !== LinkTypes.NO_LINK.value) { // Include NO_EFFECT_EXPLICIT (-1)
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            const evidenceId = evidenceNode.id;

            if (sourceId === evidenceId) {
                // This evidence links to this model
                linkedModels.add(targetId);
            } else if (targetId === evidenceId) {
                // This model links to this evidence
                linkedModels.add(sourceId);
            }
        }
    });

    return linkedModels.size === modelNodes.length;
};

/**
 * Gets the node calculated support strength
 */
/**
 * Gets the node calculated support strength
 */
export const calculateSupportStrength = (node, links) => {
    if (!links || !node) return 0;

    return links.reduce((acc, link) => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        const nodeId = node.id;

        // Only count links connected to this node
        if (sourceId === nodeId || targetId === nodeId) {
            // Skip NO_EFFECT_EXPLICIT (-1) in strength calculations
            return acc + (link.value === LinkTypes.NO_EFFECT_EXPLICIT.value ? 0 : link.value);
        }

        return acc;
    }, 0);
};

/**
 * Gets the appropriate node radius based on node type and support strength
 */
export const calculateNodeRadius = (node, links) => {
    const baseRadius = NodeConfig.baseSize;
    if (!links || node.type === "evidence") return baseRadius;

    const supportStrength = calculateSupportStrength(node, links);
    return Math.max(baseRadius, baseRadius + supportStrength * NodeConfig.supportRadiusIncrease);
};

/**
 * Creates arrow marker definitions for the SVG
 */
export const defineArrowMarkers = (svg) => {
    const defs = svg.select("defs");
    if (defs.empty()) {
        svg.append("defs");
    }

    svg.select("defs").selectAll("marker").remove();

    const markerData = [
        { id: "end-arrow", color: "#999", refX: 12, width: 6, height: 6 },
        { id: "end-arrow-selected", color: "#ff0000", refX: 12, width: 6, height: 6 },
        { id: "end-arrow-strong", color: "#999", refX: 10, width: 8, height: 8 },
        { id: "end-arrow-strong-selected", color: "#ff0000", refX: 15, width: 8, height: 8 }
    ];

    svg.select("defs")
        .selectAll("marker")
        .data(markerData)
        .join("marker")
        .attr("id", d => d.id)
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", d => d.refX)
        .attr("refY", 0)
        .attr("markerWidth", d => d.width)
        .attr("markerHeight", d => d.height)
        .attr("orient", "auto")
        .append("path")
        .attr("d", "M0,-5L10,0L0,5")
        .attr("fill", d => d.color);
};

// Export common utility functions
// Export common utility functions
export const utilities = {
    validateNodeData: (node) => {
        return node && typeof node === 'object' &&
            'id' in node &&
            'type' in node;
    },

    deepCloneData: (data) => {
        if (!data) return null;
        return JSON.parse(JSON.stringify(data));
    },

    /**
     * Recalculates and updates plausibility values for all model nodes
     * @param {Object} data - MEL data object with nodes and links
     * @returns {Object} Updated MEL data with plausibility values
     */
    recalculatePlausibility: (data) => {
        if (!data || !data.nodes || !data.links) return data;

        // Calculate plausibility for each model node
        data.nodes.forEach(node => {
            if (node && node.type === 'model') {
                // Calculate and assign plausibility directly to the node
                node.plausibility = calculateSupportStrength(node, data.links);
            }
        });

        return data;
    }
};